import React, { useEffect, useState } from "react";
import { Box, Slider, VStack } from "@metrea-ui/v2";

import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { applyFormatToNumber } from "src/utils/formatUtils";
import { mColor } from "src/utils/themeUtils";
import { debugLog } from "src/utils/debugUtils";

const VerticalRangeSlider = (props) => {
	const minValue = props.range === "normalized" ? 0 : props.extra.max_budget_decrease * -1;
	const maxValue = props.range === "normalized" ? 20 : props.extra.max_budget_increase * 1;
	const midValue = (maxValue - minValue) / 2;
	const decimalPoint = props.range !== "normalized" ? 0 : 1;
	const currencyUnit = props.range !== "normalized" ? (props.extra?.currencyUnit ?? "") : "";
	const currencySymbol = props.range !== "normalized" ? (props.extra?.currencySymbol ?? "") : "";

	const safeItems = Array.isArray(props.items) ? props.items : [];
	const sliderCursor = props.mode !== "edit" ? "not-allowed" : "pointer";
	const topLabel =
		props.range === "normalized"
			? "+1.0"
			: applyFormatToNumber(maxValue, decimalPoint, currencySymbol, currencyUnit);
	const bottomLabel =
		props.range === "normalized"
			? "-1.0"
			: applyFormatToNumber(minValue, decimalPoint, currencySymbol, currencyUnit);

	const sliderValueToItemValue = (n) => ((n - midValue) / midValue).toFixed(decimalPoint);

	const [items, setItems] = useState(safeItems);
	const [values, setValues] = useState(
		getValuesByPropertyPath(safeItems, "value").map((x) =>
			props.range === "normalized" ? x * midValue + midValue : x * 1,
		),
	);

	useEffect(() => {
		if (!Array.isArray(props.items)) return;

		setItems(props.items);
		setValues(
			getValuesByPropertyPath(props.items, "value").map((x) =>
				props.range === "normalized" ? x * midValue + midValue : x * 1,
			),
		);
	}, [props.items, props.range, midValue]);

	const valuetext = (value, index) => {
		if (!items[index]) return null;

		const label = `${items[index].type}:`;
		const displayValue =
			props.range === "normalized"
				? applyFormatToNumber(value, decimalPoint)
				: applyFormatToNumber(value, decimalPoint, currencySymbol, currencyUnit);

		return (
			<>
				<div>{label}</div>
				<div>{displayValue}</div>
			</>
		);
	};

	const sliderChangeHandler = (nextValues) => {
		if (!Array.isArray(nextValues) || nextValues.length !== 2) return;

		setValues(nextValues);

		debugLog(8, "[VerticalRangeSlider][ACTION] onValueChange", {
			nextValues,
			oldItems: items,
		});
	};

	const sliderDoneHandler = (finalValues) => {
		if (!Array.isArray(finalValues) || finalValues.length !== 2) return;

		let orderedItems = [...items];
		let orderedValues = [...finalValues];

		const nearIndex = items.findIndex((i) => i.type === "Near-term");
		const longIndex = items.findIndex((i) => i.type === "Long-term");

		if (nearIndex !== 0 || longIndex !== 1) {
			orderedItems = [
				items.find((i) => i.type === "Near-term"),
				items.find((i) => i.type === "Long-term"),
			];
			orderedValues = [finalValues[nearIndex], finalValues[longIndex]];
		}

		setValues(orderedValues);

		const newItemValues = orderedValues.map((v) =>
			props.range === "normalized" ? sliderValueToItemValue(v) : v.toString(),
		);

		orderedItems = orderedItems.map((x, index) => ({
			...x,
			value: newItemValues[index],
		}));

		setItems(orderedItems);
		props.onChange?.(orderedItems);

		debugLog(3, "[VerticalRangeSlider][ACTION] onValueChangeEnd", {
			finalValues,
			orderedValues,
			oldItems: items,
			newItems: orderedItems,
		});
	};

	const safeValues = Array.isArray(values) && values.length === 2 ? values : [minValue, maxValue];
	const semantic = {
		near: items.findIndex((i) => i.type === "Near-term"),
		long: items.findIndex((i) => i.type === "Long-term"),
	};

	return (
		<VStack spacing={1} align="stretch" height={props.height || 200} m={props.m || 2}>
			<Box position="relative" height="100%" display="flex" justifyContent="center">
				<Slider.Root
					orientation="vertical"
					value={safeValues}
					min={minValue}
					max={maxValue}
					step={props.step || 1}
					disabled={props.mode !== "edit"}
					height="100%"
					minStepsBetweenThumbs={-(maxValue - minValue)}
					onValueChange={(e) => sliderChangeHandler(e.value)}
					onValueChangeEnd={(e) => sliderDoneHandler(e.value)}
				>
					<Slider.Control height="100%">
						<Slider.Track style={{ cursor: sliderCursor }}>
							<Slider.Range bg={mColor.SkyBlue} />
						</Slider.Track>

						{safeValues.map((value, index) => (
							<Slider.Thumb
								key={index}
								index={index}
								borderColor={semantic.near === index ? mColor.StonyBlue : mColor.NavyBlue}
								bg={semantic.near === index ? mColor.StonyBlue : mColor.NavyBlue}
								style={{ cursor: sliderCursor }}
							>
								<Slider.HiddenInput />
							</Slider.Thumb>
						))}
					</Slider.Control>
				</Slider.Root>

				{safeValues.map((value, visualIndex) => {
					const isNear = semantic.near === visualIndex;

					return (
						<Box
							key={visualIndex}
							position="absolute"
							top={`${((maxValue - value) / (maxValue - minValue)) * 100}%`}
							left={isNear ? "-70px" : "30px"}
							transform="translateY(-50%)"
							fontSize="10px"
							bg={isNear ? mColor.StonyBlue : mColor.NavyBlue}
							color="white"
							px="4px"
							py="2px"
							borderRadius="4px"
							whiteSpace="nowrap"
							pointerEvents="none"
							textAlign="center"
							alignItems="center"
						>
							{valuetext(sliderValueToItemValue(value) * 1, visualIndex)}
						</Box>
					);
				})}

				{/* Keep the fixed rail labels anchored just to the right of the track so
				    the two-thumb slider reads like the single-thumb budget slider. */}
				<Box
					position="absolute"
					left="calc(50% + 22px)"
					top="0"
					fontSize="11px"
					color="gray.600"
					textAlign="left"
					pointerEvents="none"
					whiteSpace="nowrap"
				>
					{topLabel}
				</Box>
				<Box
					position="absolute"
					left="calc(50% + 22px)"
					bottom="0"
					fontSize="11px"
					color="gray.600"
					textAlign="left"
					pointerEvents="none"
					whiteSpace="nowrap"
				>
					{bottomLabel}
				</Box>
			</Box>
		</VStack>
	);
};

export default VerticalRangeSlider;
